import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentService } from '../services/paymentService';
import authService from '../services/authService';
import './MyPaymentsPage.css';

const MyPaymentsPage = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const normalizeStatus = (status) => {
    if (!status) return 'pending';
    const s = status.toLowerCase();
    if (s === 'aceptada' || s === 'approved' || s === 'completed') return 'approved';
    if (s === 'rechazada' || s === 'rejected') return 'rejected';
    if (s === 'pendiente' || s === 'pending') return 'pending';
    if (s === 'fallida' || s === 'failed') return 'failed';
    if (s === 'reversada' || s === 'reversed' || s === 'refunded') return 'reversed';
    return 'pending';
  };

  useEffect(() => {
    const user = authService.getUser();
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/mis-pagos' } } });
      return;
    }

    const fetchPayments = async () => {
      try {
        const data = await paymentService.getPayments();
        const list = Array.isArray(data) ? data : data.payments || [];
        setPayments(list);
      } catch (err) {
        console.error('Error fetching payments:', err);
        setError('No se pudieron cargar tus pagos. Intenta de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [navigate]);

  const getStatusBadge = (status) => {
    const normalized = normalizeStatus(status);
    const config = {
      approved: { label: 'Aprobado', icon: '✅', className: 'badge-approved' },
      rejected: { label: 'Rechazado', icon: '❌', className: 'badge-rejected' },
      pending: { label: 'Pendiente', icon: '⏳', className: 'badge-pending' },
      failed: { label: 'Fallido', icon: '💥', className: 'badge-failed' },
      reversed: { label: 'Reversado', icon: '↩️', className: 'badge-reversed' },
    };
    const c = config[normalized] || config.pending;
    return <span className={`payment-badge ${c.className}`}>{c.icon} {c.label}</span>;
  };

  const getMethodLabel = (method) => {
    switch (method) {
      case 'credit_card': return '💳 Tarjeta de Crédito';
      case 'pse': return '🏦 PSE';
      case 'cash': return '💵 Efectivo';
      default: return method || 'N/A';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-CO', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency', currency: 'COP',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="my-payments-page">
        <div className="my-payments-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Cargando tus pagos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-payments-page">
      <div className="my-payments-container">
        <div className="my-payments-header">
          <h1>Mis Pagos</h1>
          <p className="subtitle">Historial de todas tus transacciones</p>
        </div>

        {error && (
          <div className="error-banner">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Reintentar</button>
          </div>
        )}

        {payments.length === 0 && !error ? (
          <div className="empty-state">
            <div className="empty-icon">🛒</div>
            <h3>No tienes pagos aún</h3>
            <p>Cuando realices una compra, aparecerá aquí.</p>
            <button className="btn-primary" onClick={() => navigate('/productos')}>
              Ver Productos
            </button>
          </div>
        ) : (
          <>
            <div className="payments-summary">
              <div className="summary-card">
                <span className="summary-label">Total de pagos</span>
                <span className="summary-value">{payments.length}</span>
              </div>
              <div className="summary-card">
                <span className="summary-label">Aprobados</span>
                <span className="summary-value summary-approved">
                  {payments.filter(p => normalizeStatus(p.status) === 'approved').length}
                </span>
              </div>
              <div className="summary-card">
                <span className="summary-label">Pendientes</span>
                <span className="summary-value summary-pending">
                  {payments.filter(p => normalizeStatus(p.status) === 'pending').length}
                </span>
              </div>
            </div>

            {/* Vista móvil: tarjetas */}
            <div className="payments-cards">
              {payments.map((payment) => (
                <div key={payment.id || payment.ID} className="payment-card">
                  <div className="card-top">
                    <span className="card-ref">#{payment.ref_epayco || payment.invoice || payment.id || payment.ID}</span>
                    {getStatusBadge(payment.status)}
                  </div>
                  <div className="card-amount">{formatCurrency(payment.amount)}</div>
                  <div className="card-details">
                    <span>{getMethodLabel(payment.payment_method)}</span>
                    <span>{formatDate(payment.transaction_date || payment.created_at)}</span>
                  </div>
                  {payment.description && (
                    <div className="card-description">{payment.description}</div>
                  )}
                </div>
              ))}
            </div>

            {/* Vista desktop: tabla */}
            <div className="payments-table-wrapper">
              <table className="payments-table">
                <thead>
                  <tr>
                    <th>Referencia</th>
                    <th>Fecha</th>
                    <th>Descripción</th>
                    <th>Método</th>
                    <th>Monto</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id || payment.ID}>
                      <td className="ref-cell">
                        {payment.ref_epayco || payment.invoice || `#${payment.id || payment.ID}`}
                      </td>
                      <td>{formatDate(payment.transaction_date || payment.created_at)}</td>
                      <td className="desc-cell">{payment.description || 'Compra en Tablero Infinito'}</td>
                      <td>{getMethodLabel(payment.payment_method)}</td>
                      <td className="amount-cell">{formatCurrency(payment.amount)}</td>
                      <td>{getStatusBadge(payment.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MyPaymentsPage;
